import type { ReactNode, ReactElement } from 'react';

/**
 * Defensive cleanup + light markdown for agent bubbles (TASK-43/44).
 *
 * The clarifier protocol emits `[CRITERIA_CONFIRMED] { …json… }`; the server
 * normally strips it, but malformed JSON can leak the raw tail into chat. We
 * strip it client-side, drop any stray CJK (language-bleed safety net), and
 * render `- bullets` / **bold** / `code` properly instead of raw asterisks.
 */
const CRITERIA_TOKEN = /\[CRITERIA_CONFIRMED\][\s\S]*$/;
// CJK + Hiragana/Katakana + Hangul ranges — a guard, not a fix (root cause is the prompt).
const CJK = /[　-〿぀-ヿㇰ-ㇿ㐀-䶿一-鿿豈-﫿가-힯]/g;

export function cleanAgentText(raw: string): string {
  return raw
    .replace(CRITERIA_TOKEN, '')
    .replace(CJK, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function inline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] != null) nodes.push(<strong key={`${keyBase}-b${i}`}>{m[2]}</strong>);
    else if (m[3] != null)
      nodes.push(
        <code
          key={`${keyBase}-c${i}`}
          style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9em', background: 'var(--bg-overlay)', padding: '1px 4px', borderRadius: 4 }}
        >
          {m[3]}
        </code>,
      );
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function AgentMarkdown({ text }: { text: string }): ReactElement {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];

  const flush = (k: string): void => {
    if (bullets.length > 0) {
      const items = bullets;
      blocks.push(
        <ul key={k} style={{ margin: '4px 0', paddingLeft: 18 }}>
          {items.map((b, j) => (
            <li key={j} style={{ margin: '2px 0' }}>{inline(b, `${k}-${j}`)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };

  text.split('\n').forEach((line, idx) => {
    const bm = line.match(/^\s*[-*]\s+(.*)$/);
    if (bm) {
      bullets.push(bm[1] ?? '');
      return;
    }
    flush(`ul-${idx}`);
    if (line.trim() === '') return;
    blocks.push(<p key={`p-${idx}`} style={{ margin: '4px 0' }}>{inline(line, `p-${idx}`)}</p>);
  });
  flush('ul-end');

  return <>{blocks}</>;
}
